import { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { trpc } from '../../api/trpc';
import '../../styles/crypto-inventory.css';
import { buildInvestigateSeed } from './crypto-investigate-seed';

// Human labels for source_service (spec §7.1 "Source" column).
const SOURCE_LABELS: Record<string, string> = {
  app_registration: 'App registration cert',
  service_principal: 'Service principal cert',
  service_principal_secret: 'Client secret',
  saml_signing: 'SAML signing cert',
  cba_trusted_ca: 'CBA trusted CA',
  domain_federation: 'Federation signing cert',
  kms_key: 'KMS key',
  iam_server_cert: 'IAM server cert',
  acm_cert: 'ACM certificate',
  oidc_provider: 'OIDC provider',
  ec2_keypair: 'EC2 key pair',
};

const BAND_TO_SEV: Record<string, string> = {
  critical: 'sev-critical',
  high: 'sev-high',
  medium: 'sev-medium',
  low: 'sev-low',
};

interface BlastRadiusSnapshot {
  dependents: number;
  privileged: boolean;
  // M added in v1.0.1 (management dampener). Older snapshots may lack it.
  factors: { B: number; Br: number; Ag: number; Ex: number; M?: number };
}

// The API is proxied through the gateway (loosely typed), so we describe the
// row / summary shape locally — it mirrors inventory-repository.ts.
interface CryptoAssetRow {
  id: string;
  platform: string;
  platformAccount: string;
  sourceService: string;
  externalId: string;
  keyAlgorithm: string;
  keySize: number | null;
  threatScenario: string;
  isTokenSigning: boolean;
  createdAtSource: string | null;
  notAfter: string | null;
  displayHints: unknown;
  // §4.1 semantic columns (req 822).
  materialKind: string;
  privateKeyHolder: string;
  managedBy: 'customer' | 'platform';
  functionSummary: string;
  priorityScore: number | null;
  priorityScoreRaw: number | null;
  priorityBand: 'critical' | 'high' | 'medium' | 'low' | null;
  blastRadiusSnapshot: unknown;
  status: string;
}

interface InventorySummary {
  quantumVulnerableCount: number;
  byScenario: Record<string, number>;
  byBand: Record<string, number>;
  platformManagedCount: number;
  latestRun: { status: string; platformCoverage: unknown } | null;
  hasActiveConnector: boolean;
}

// Human labels for the §4.1 semantic enums — rendered in the row drawer so
// the operator sees "AWS holds the private key" rather than the raw enum.
const MATERIAL_KIND_LABEL: Record<string, string> = {
  certificate_public: 'Public certificate (identity anchor)',
  certificate_private_at_provider: 'Certificate; private key at provider',
  hsm_key: 'HSM-bound key (non-exportable)',
  public_key_only: 'Public key only',
  shared_secret: 'Shared secret',
};
const PRIVATE_KEY_HOLDER_LABEL: Record<string, string> = {
  customer: 'You (your workload holds the private key)',
  microsoft: 'Microsoft (Entra holds the private key)',
  aws: 'AWS (KMS / ACM / IAM holds the private key)',
  external_idp: 'External identity provider',
  certificate_authority: 'External certificate authority',
};
const MANAGED_BY_ROTATION_LABEL: Record<string, string> = {
  customer: 'You rotate this material.',
  platform: 'Vendor rotates this material on their own schedule.',
};

type Hints = Record<string, unknown>;

function hintStr(h: Hints, key: string): string | null {
  const v = h[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * Preferred asset name for the Name column. §4.1 amendment: prefer the owning
 * entity's display name (app/SP/domain) over the raw certificate subject CN,
 * so a row reads as "Contoso HR SAML" rather than "ADFS-2016-signing". Subject
 * CN moves to the drawer via `assetSubjectCn`.
 */
function assetName(row: { displayHints: unknown; externalId: string }): string {
  const h = (row.displayHints ?? {}) as Hints;
  return (
    hintStr(h, 'displayName') ??
    hintStr(h, 'appDisplayName') ??
    hintStr(h, 'domain') ??
    hintStr(h, 'domainName') ??
    hintStr(h, 'alias') ??
    hintStr(h, 'serverCertificateName') ??
    hintStr(h, 'keyName') ??
    hintStr(h, 'url') ??
    hintStr(h, 'subjectCn') ?? // last resort before the raw id
    row.externalId
  );
}

function assetSubjectCn(row: { displayHints: unknown }): string | null {
  return hintStr((row.displayHints ?? {}) as Hints, 'subjectCn');
}

function assetThumbprint(row: { displayHints: unknown }): string | null {
  const h = (row.displayHints ?? {}) as Hints;
  return (
    hintStr(h, 'thumbprintSha256') ??
    hintStr(h, 'thumbprintSha1') ??
    hintStr(h, 'customKeyIdentifier')
  );
}

function algoLabel(algorithm: string, keySize: number | null): string {
  const base = algorithm.replace('_', '-').toUpperCase();
  return keySize ? `${base}-${keySize}` : base;
}

function fmtDate(d: string | Date | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString();
}

interface PopoverState {
  id: string;
  x: number;
  y: number;
  band: string | null;
  score: number | null;
  snapshot: BlastRadiusSnapshot | null;
}

export function CryptoInventoryPage() {
  const [search, setSearch] = useState('');
  const [quantumVulnerableOnly, setQuantumVulnerableOnly] = useState(true);
  const [platform, setPlatform] = useState('');
  const [sourceService, setSourceService] = useState('');
  const [threatScenario, setThreatScenario] = useState('');
  const [priorityBand, setPriorityBand] = useState('');
  const [status, setStatus] = useState('');
  // §4.1 default: hide vendor-managed material. Off = customer-only view;
  // on = include the platform-managed rows (Entra device-auth family).
  const [showVendorManaged, setShowVendorManaged] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);

  const listInput = useMemo(
    () => ({
      quantumVulnerableOnly,
      ...(platform ? { platform: platform as 'entra' | 'aws' } : {}),
      ...(sourceService ? { sourceService } : {}),
      ...(threatScenario ? { threatScenario: threatScenario as 'forgery' | 'harvest' | 'both' | 'none' } : {}),
      ...(priorityBand ? { priorityBand: priorityBand as 'critical' | 'high' | 'medium' | 'low' } : {}),
      ...(status ? { status: [status as 'active' | 'expiring' | 'expired' | 'removed_at_source'] } : {}),
      ...(search ? { search } : {}),
      // undefined → customer-only default; null → include vendor-managed.
      ...(showVendorManaged ? { managedBy: null } : {}),
      limit: pageSize,
      offset: page * pageSize,
    }),
    [quantumVulnerableOnly, platform, sourceService, threatScenario, priorityBand, status, search, showVendorManaged, pageSize, page],
  );

  // While a manual run is in flight, poll both queries so the table fills in
  // as the run produces rows.
  const refetchInterval = polling ? 5000 : false;
  const { data, isLoading } = trpc.posture.cryptoInventory.listAssets.useQuery(listInput, { refetchInterval });
  const { data: summaryData } = trpc.posture.cryptoInventory.summary.useQuery(undefined, { refetchInterval });

  const runNow = trpc.posture.cryptoInventory.runDiscoveryNow.useMutation();

  // "Investigate with Cade" flow — create a conversation, seed it with the
  // row's four §4.1 lines, and navigate. Cade's `crypto_list_assets` tool
  // registered on the orchestrator lets it pivot to related rows without
  // more clicks from the analyst.
  const navigate = useNavigate();
  const createConv = trpc.conversations.create.useMutation();
  const sendMessage = trpc.conversations.sendMessage.useMutation();
  const [investigating, setInvestigating] = useState<string | null>(null);

  const handleInvestigate = useCallback(
    async (row: CryptoAssetRow) => {
      if (investigating) return;
      setInvestigating(row.id);
      try {
        const conv = await createConv.mutateAsync();
        const seed = buildInvestigateSeed({
          name: assetName(row),
          sourceLabel: SOURCE_LABELS[row.sourceService] ?? row.sourceService,
          platform: row.platform,
          platformAccount: row.platformAccount,
          materialKindLabel: MATERIAL_KIND_LABEL[row.materialKind] ?? row.materialKind,
          privateKeyHolderLabel: PRIVATE_KEY_HOLDER_LABEL[row.privateKeyHolder] ?? row.privateKeyHolder,
          rotationLabel: MANAGED_BY_ROTATION_LABEL[row.managedBy] ?? row.managedBy,
          functionSummary: row.functionSummary,
          algorithm: row.keyAlgorithm === 'unknown_x509' ? 'unknown' : algoLabel(row.keyAlgorithm, row.keySize),
          priorityBand: row.priorityBand,
          priorityScore: row.priorityScore,
          threatScenario: row.threatScenario,
          isTokenSigning: row.isTokenSigning,
          notAfter: row.notAfter ? fmtDate(row.notAfter) : null,
          status: row.status,
          managedBy: row.managedBy,
          // keyNodeId is only present when graph population has run for this row.
          // We forward it via the extended row shape below.
          keyNodeId: (row as CryptoAssetRow & { keyNodeId?: string | null }).keyNodeId ?? null,
        });
        await sendMessage.mutateAsync({
          conversationId: conv.id,
          content: seed,
          surface: 'web',
        });
        navigate(`/chat/${conv.id}`);
      } finally {
        setInvestigating(null);
      }
    },
    [createConv, sendMessage, navigate, investigating],
  );

  async function handleRunDiscovery() {
    try {
      await runNow.mutateAsync();
      setPolling(true);
      // Discovery reads live Graph/AWS and can take a while on large tenants;
      // poll for a bounded window, then stop. Run outcome (incl. failure) also
      // surfaces via the coverage indicator on the next summary refresh.
      window.setTimeout(() => setPolling(false), 90_000);
    } catch {
      // surfaced via runNow.isError below
    }
  }

  const listData = data as { rows?: CryptoAssetRow[]; total?: number } | undefined;
  const summary = summaryData as InventorySummary | undefined;
  const rows = listData?.rows ?? [];
  const total = listData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const resetPage = () => setPage(0);
  const coverage = coverageState(summary?.latestRun);
  const filtersActive = !!(search || platform || sourceService || threatScenario || priorityBand || status);

  return (
    <div className="page-scroll" onClick={() => popover && setPopover(null)}>
      <div className="page-inner" style={{ maxWidth: 1100 }}>
        <div className="ci-header">
          <div>
            <div className="page-title">Cryptographic Inventory</div>
            <div className="ci-subtitle">
              Quantum-vulnerable keys and certificates across your connected platforms.{' '}
              <Link to="/">← Back to chat</Link>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {polling && <span className="ci-subtitle">Discovery running…</span>}
            {runNow.isError && (
              <span style={{ color: 'var(--sev-critical)', fontSize: 12 }}>
                {runNow.error?.message ?? 'Failed to start'}
              </span>
            )}
            <button
              className="btn btn-recommended btn-sm"
              disabled={runNow.isPending || polling}
              onClick={handleRunDiscovery}
              title="Run a discovery pass for your tenant now"
            >
              {runNow.isPending || polling ? 'Running…' : 'Run discovery now'}
            </button>
          </div>
        </div>

        {/* Header widgets */}
        <div className="ci-widgets">
          <div className="ci-widget">
            <div className="ci-widget-value">{summary?.quantumVulnerableCount ?? '—'}</div>
            <div className="ci-widget-label">Quantum-vulnerable</div>
          </div>
          <div className="ci-widget">
            <div className="ci-widget-label">By scenario</div>
            <div className="ci-widget-chips">
              {summary
                ? Object.entries(summary.byScenario).map(([k, v]) => (
                    <span key={k} className={`ci-badge ci-scenario-${k}`}>
                      {k} {v}
                    </span>
                  ))
                : '—'}
            </div>
          </div>
          <div className="ci-widget">
            <div className="ci-widget-label">By priority</div>
            <div className="ci-widget-chips">
              {summary
                ? (['critical', 'high', 'medium', 'low'] as const)
                    .filter((b) => summary.byBand[b])
                    .map((b) => (
                      <span key={b} className={`status-badge ${BAND_TO_SEV[b]}`}>
                        {b} {summary.byBand[b]}
                      </span>
                    ))
                : '—'}
            </div>
          </div>
          <div className={`ci-coverage ci-coverage-${coverage.tone}`}>
            <span className="ci-coverage-dot" />
            <span>{coverage.label}</span>
            {coverage.tone === 'partial' || coverage.tone === 'failed' ? (
              <Link className="ci-coverage-link" to="/settings?tab=connectors">
                Fix coverage
              </Link>
            ) : null}
          </div>
        </div>

        {/* Filters */}
        <div className="ci-filters">
          <input
            className="audit-filter-input ci-search"
            placeholder="Search name, thumbprint, ARN…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
          />
          <label className="ci-toggle">
            <input
              type="checkbox"
              checked={quantumVulnerableOnly}
              onChange={(e) => {
                setQuantumVulnerableOnly(e.target.checked);
                resetPage();
              }}
            />
            Quantum-vulnerable only
          </label>
          <label
            className="ci-toggle"
            title="Off (default): hide device-auth certificates that the vendor rotates on their own schedule. On: include them."
          >
            <input
              type="checkbox"
              checked={showVendorManaged}
              onChange={(e) => {
                setShowVendorManaged(e.target.checked);
                resetPage();
              }}
            />
            Include vendor-managed
            {summary && summary.platformManagedCount > 0 && (
              <span className="ci-vendor-count"> ({summary.platformManagedCount})</span>
            )}
          </label>
          <select className="audit-filter-select" value={platform} onChange={(e) => { setPlatform(e.target.value); resetPage(); }}>
            <option value="">All platforms</option>
            <option value="entra">Entra ID</option>
            <option value="aws">AWS</option>
          </select>
          <select className="audit-filter-select" value={sourceService} onChange={(e) => { setSourceService(e.target.value); resetPage(); }}>
            <option value="">All sources</option>
            {Object.entries(SOURCE_LABELS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
          <select className="audit-filter-select" value={threatScenario} onChange={(e) => { setThreatScenario(e.target.value); resetPage(); }}>
            <option value="">All scenarios</option>
            <option value="forgery">Forgery</option>
            <option value="harvest">Harvest</option>
            <option value="both">Both</option>
            <option value="none">None</option>
          </select>
          <select className="audit-filter-select" value={priorityBand} onChange={(e) => { setPriorityBand(e.target.value); resetPage(); }}>
            <option value="">All priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select className="audit-filter-select" value={status} onChange={(e) => { setStatus(e.target.value); resetPage(); }}>
            <option value="">Active + expiring + expired</option>
            <option value="active">Active</option>
            <option value="expiring">Expiring</option>
            <option value="expired">Expired</option>
            <option value="removed_at_source">Removed at source</option>
          </select>
        </div>

        {/* Table */}
        <div className="ci-table">
          <div className="ci-th">
            <span>Name</span>
            <span>Platform / Account</span>
            <span>Source</span>
            <span>Algorithm</span>
            <span>Scenario</span>
            <span>Created / Expires</span>
            <span>Priority</span>
            <span>Status</span>
          </div>

          {isLoading && <div className="ci-loading">Loading inventory…</div>}
          {!isLoading && rows.length === 0 && (
            <div className="ci-empty">{renderEmptyState({ filtersActive, summary })}</div>
          )}

          {!isLoading &&
            rows.map((r) => {
              const expired = r.status === 'expired';
              const isExpanded = expandedRowId === r.id;
              const name = assetName(r);
              return (
                <div key={r.id}>
                  <div className="ci-row" onClick={(e) => {
                    // Row clicks toggle the drawer, but not when clicking the
                    // priority button (which owns its own popover) or a link.
                    const target = e.target as HTMLElement;
                    if (target.closest('.ci-priority') || target.closest('a')) return;
                    setExpandedRowId(isExpanded ? null : r.id);
                  }}>
                    <span className="ci-name" title={r.externalId}>
                      <button
                        className="ci-drawer-caret"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse asset details' : 'Expand asset details'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedRowId(isExpanded ? null : r.id);
                        }}
                      >
                        {isExpanded ? '▾' : '▸'}
                      </button>
                      <span className="ci-name-text">{name}</span>
                      {r.managedBy === 'platform' && (
                        <span className="ci-vendor-badge" title="Vendor-issued and vendor-rotated. You cannot rotate this material; the vendor moves it to PQC on their own schedule.">
                          vendor-managed
                        </span>
                      )}
                    </span>
                    <span className="ci-mono">{r.platform} · {r.platformAccount}</span>
                    <span>{SOURCE_LABELS[r.sourceService] ?? r.sourceService}</span>
                    <span className="ci-mono">
                      {r.keyAlgorithm === 'unknown_x509' ? (
                        <span className="ci-unknown-alg" title="Public key unreadable — treated as quantum-vulnerable">unknown</span>
                      ) : (
                        algoLabel(r.keyAlgorithm, r.keySize)
                      )}
                    </span>
                    <span>
                      <span className={`ci-badge ci-scenario-${r.threatScenario}`}>{r.threatScenario}</span>
                      {r.isTokenSigning && <span className="ci-token-marker" title="Token-signing (Storm-0558 class)"> ⚑</span>}
                    </span>
                    <span className={`ci-mono ${expired ? 'ci-expired' : ''}`}>
                      {fmtDate(r.createdAtSource)} → {fmtDate(r.notAfter)}
                    </span>
                    <span>
                      {r.priorityBand ? (
                        <button
                          className="ci-priority"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPopover({
                              id: r.id,
                              x: e.clientX,
                              y: e.clientY,
                              band: r.priorityBand,
                              score: r.priorityScore,
                              snapshot: (r.blastRadiusSnapshot ?? null) as BlastRadiusSnapshot | null,
                            });
                          }}
                          title="Why this band?"
                        >
                          <span className={`status-badge ${BAND_TO_SEV[r.priorityBand]}`}>{r.priorityBand}</span>
                          {r.priorityScore != null && <span className="ci-priority-score">{r.priorityScore}</span>}
                        </button>
                      ) : (
                        <span className="ci-priority-score">unscored</span>
                      )}
                    </span>
                    <span className="ci-mono">{r.status}</span>
                  </div>
                  {isExpanded && (
                    <AssetDrawer
                      row={r}
                      onInvestigate={() => void handleInvestigate(r)}
                      investigating={investigating === r.id}
                    />
                  )}
                </div>
              );
            })}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="ci-pagination">
            <span>
              Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}
            </span>
            <div className="ci-pagination-controls">
              <select
                className="audit-filter-select"
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); resetPage(); }}
              >
                {[25, 50, 100].map((s) => (
                  <option key={s} value={s}>{s} / page</option>
                ))}
              </select>
              <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <button className="btn btn-secondary btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Explainability popover (req 809) — reads blast_radius_snapshot, no client recompute */}
      {popover && (
        <>
          <div className="ci-popover-backdrop" onClick={() => setPopover(null)} />
          <div
            className="ci-popover"
            style={{ left: Math.min(popover.x, window.innerWidth - 320), top: popover.y + 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ci-popover-title">
              Why {popover.band}? {popover.score != null && <span className="ci-priority-score">score {popover.score}</span>}
            </div>
            <div className="ci-popover-formula">score = clamp(B × Br × Ag × Ex × M, 0, 100)</div>
            {popover.snapshot?.factors ? (
              <>
                <div className="ci-factor"><span className="ci-factor-key">B — scenario base</span><span className="ci-factor-val">{popover.snapshot.factors.B}</span></div>
                <div className="ci-factor"><span className="ci-factor-key">Br — blast radius</span><span className="ci-factor-val">×{popover.snapshot.factors.Br}</span></div>
                <div className="ci-factor"><span className="ci-factor-key">Ag — age</span><span className="ci-factor-val">×{popover.snapshot.factors.Ag}</span></div>
                <div className="ci-factor"><span className="ci-factor-key">Ex — exposure</span><span className="ci-factor-val">×{popover.snapshot.factors.Ex}</span></div>
                {popover.snapshot.factors.M != null && (
                  <div className="ci-factor"><span className="ci-factor-key">M — management</span><span className="ci-factor-val">×{popover.snapshot.factors.M}</span></div>
                )}
                <div className="ci-factor"><span className="ci-factor-key">dependents (1-hop)</span><span className="ci-factor-val">{popover.snapshot.dependents}</span></div>
                <div className="ci-factor"><span className="ci-factor-key">privileged owner</span><span className="ci-factor-val">{popover.snapshot.privileged ? 'yes' : 'no'}</span></div>
              </>
            ) : (
              <div className="ci-factor-key">No score breakdown recorded yet.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function renderEmptyState({
  filtersActive,
  summary,
}: {
  filtersActive: boolean;
  summary: InventorySummary | undefined;
}) {
  if (filtersActive) return 'No cryptographic assets match these filters.';
  if (summary && !summary.hasActiveConnector) {
    return (
      <>
        No Entra ID or AWS connector is connected, so there is nothing to discover yet.{' '}
        <Link to="/settings?tab=connectors">Connect a connector</Link>, then click “Run discovery now”.
      </>
    );
  }
  if (summary && !summary.latestRun) {
    return 'No discovery has run yet — click “Run discovery now” to scan your connected platforms.';
  }
  return 'No quantum-vulnerable assets found.';
}

/**
 * Row drawer — the four §4.1 labeled lines that answer "what is this / what
 * does it serve / who holds the private key / who rotates it", plus a single
 * "Investigate with Cade" action that opens a seeded conversation.
 */
function AssetDrawer({
  row,
  onInvestigate,
  investigating,
}: {
  row: CryptoAssetRow;
  onInvestigate: () => void;
  investigating: boolean;
}) {
  const cn = assetSubjectCn(row);
  const thumbprint = assetThumbprint(row);
  const hints = (row.displayHints ?? {}) as Record<string, unknown>;
  const enrichmentSource = typeof hints.enrichmentSource === 'string' ? hints.enrichmentSource : null;
  return (
    <div className="ci-drawer">
      <div className="ci-drawer-lines">
        <DrawerLine label="What this is" value={MATERIAL_KIND_LABEL[row.materialKind] ?? row.materialKind} />
        <DrawerLine label="What it serves" value={row.functionSummary || '—'} />
        <DrawerLine
          label="Who holds the private key"
          value={PRIVATE_KEY_HOLDER_LABEL[row.privateKeyHolder] ?? row.privateKeyHolder}
        />
        <DrawerLine label="Who rotates it" value={MANAGED_BY_ROTATION_LABEL[row.managedBy] ?? row.managedBy} />
      </div>
      <div className="ci-drawer-meta">
        {cn && (
          <div className="ci-drawer-meta-line">
            <span className="ci-drawer-meta-key">Subject CN</span>
            <span className="ci-drawer-meta-val ci-mono">{cn}</span>
          </div>
        )}
        {thumbprint && (
          <div className="ci-drawer-meta-line">
            <span className="ci-drawer-meta-key">Thumbprint</span>
            <span className="ci-drawer-meta-val ci-mono" title={thumbprint}>
              {thumbprint.length > 16 ? `${thumbprint.slice(0, 16)}…` : thumbprint}
            </span>
          </div>
        )}
        <div className="ci-drawer-meta-line">
          <span className="ci-drawer-meta-key">Source ID</span>
          <span className="ci-drawer-meta-val ci-mono">{row.externalId}</span>
        </div>
        {enrichmentSource && (
          <div className="ci-drawer-meta-line" title="Algorithm / size resolved from a public metadata endpoint after Graph withheld the public key.">
            <span className="ci-drawer-meta-key">Enriched from</span>
            <span className="ci-drawer-meta-val ci-mono">{enrichmentSource}</span>
          </div>
        )}
        <div className="ci-drawer-actions">
          <button
            type="button"
            className="btn btn-recommended btn-sm"
            onClick={onInvestigate}
            disabled={investigating}
            title="Open a new Cade conversation seeded with this asset's context."
          >
            {investigating ? 'Opening…' : 'Investigate with Cade'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DrawerLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="ci-drawer-line">
      <span className="ci-drawer-line-label">{label}</span>
      <span className="ci-drawer-line-value">{value}</span>
    </div>
  );
}

function coverageState(
  run: { status: string; platformCoverage: unknown } | null | undefined,
): { tone: 'ok' | 'partial' | 'failed' | 'none'; label: string } {
  if (!run) return { tone: 'none', label: 'No discovery run yet' };
  if (run.status === 'completed') return { tone: 'ok', label: 'Coverage complete' };
  if (run.status === 'partial') return { tone: 'partial', label: 'Partial coverage — a surface was skipped' };
  if (run.status === 'failed') return { tone: 'failed', label: 'Last discovery run failed' };
  return { tone: 'none', label: `Discovery ${run.status}` };
}
